import clsx from 'clsx';
import * as React from 'react';

import { isDocumentEmpty, trimDocument } from '@gitbook/doc-core';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import type { Change, Value } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';

import { BasicEditor, type BasicEditorProps, InsertPageMentionButton } from '../BasicEditor';
import { useContentReferenceContext } from '../ContentReferences';

const mentions: BasicEditorProps['mentions'] = {
    '#': ['page'],
    '@': ['page'],
};

const noMentions: BasicEditorProps['mentions'] = {};

const AGENT_CHAT_SUGGESTIONS: Array<{
    label: string;
    prompt: string;
}> = [
    {
        label: 'Improve a page',
        prompt: 'Suggest improvements (readability, grammar, etc.) to page #',
    },
    {
        label: 'Add usage examples',
        prompt: 'Add practical usage examples to enhance understanding of the functions on page #',
    },
    {
        label: 'Optimize for SEO',
        prompt: 'Optimize the content for better search engine visibility by using relevant keywords on page #',
    },
    {
        label: 'Enhance clarity',
        prompt: 'Rephrase complex sections for better understanding and clarity on page #',
    },
    {
        label: 'Check for consistency',
        prompt: 'Read some pages and check and enforce consistency for terminology and style with the rest of the documentation on page #',
    },
    {
        label: 'Link related topics',
        prompt: 'Identify and link related topics to improve navigation on page #',
    },
    {
        label: 'Create fun intro',
        prompt: 'Review the first pages and create a new intro page introducing the documentation in a fun way',
    },
];

/**
 * Input to post a new message in the chat.
 */
export function AgentChatInput(props: {
    value: Value;
    setValue: React.Dispatch<React.SetStateAction<Value>>;

    /**
     * Whether the input is editable.
     */
    editable?: boolean;

    /**
     * Whether the input is submittable.
     */
    submittable?: boolean;

    /**
     * Placeholder for the input.
     */
    placeholder?: string;

    /**
     * Callback to submit the input.
     */
    onSubmit?: () => void;

    /**
     * Class name to apply to the input.
     */
    className?: string;
}) {
    const {
        value,
        editable = true,
        submittable = true,
        setValue,
        className,
        onSubmit,
        placeholder = 'Plan, search, ask, write anything',
    } = props;

    const contentRefContext = useContentReferenceContext();
    const isEmpty = React.useMemo(() => {
        return isDocumentEmpty(trimDocument(value.document));
    }, [value.document]);

    const onChange = (change: Change) => {
        setValue(change.value);
    };

    const onReplaceText = (text: string) => {
        if (editable) {
            setValue(
                (prevValue) =>
                    prevValue.change().selectAll().delete().insertText(text).focus().value
            );
        }
    };

    React.useEffect(() => {
        setValue((value) => value.change().collapseToEndOf(value.document).focus().value);
    }, [setValue]);

    return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
        <div
            className={clsx('flex cursor-text flex-col gap-3', className)}
            onClick={() => onChange(value.change().focus())}
        >
            <BasicEditor
                value={value}
                onChange={onChange}
                readOnly={!editable}
                placeholder={placeholder}
                className="px-4 py-3"
                mentions={contentRefContext.space ? mentions : noMentions}
                onSubmit={submittable ? onSubmit : undefined}
            />
            <div className="flex justify-between gap-3 px-3 pb-2">
                <div className="flex">
                    {contentRefContext.space ? (
                        <>
                            <InsertPageMentionButton readOnly={!editable} setValue={setValue} />
                            <PaletteButton
                                size="medium"
                                kind="blank"
                                label="Examples"
                                leadingIcon={Icon.AIImproveWriting}
                                trailingIcon={Icon.ChevronDown}
                                iconOnly
                                palette={() =>
                                    createPalette({
                                        groups: [
                                            {
                                                key: 'suggestions',
                                                commands: AGENT_CHAT_SUGGESTIONS.map(
                                                    (suggestion) => {
                                                        return {
                                                            key: suggestion.label,
                                                            title: suggestion.label,
                                                            trigger: () => {
                                                                onReplaceText(suggestion.prompt);
                                                            },
                                                        };
                                                    }
                                                ),
                                            },
                                        ],
                                    })
                                }
                            />
                        </>
                    ) : null}
                </div>
                {onSubmit ? (
                    <div>
                        <Button
                            size="medium"
                            kind="primary"
                            label="Post"
                            disabled={isEmpty || !editable || !submittable}
                            onPress={onSubmit}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
